import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { unsubscribeFromNewsletter } from "wasp/client/operations";
import { routes } from "wasp/client/router";
import { LinkButton } from "../components/ui";
import { type NewsletterUnsubscribeResult } from "./unsubscribe";

type PageState =
  | { status: "loading" }
  | {
      status: "success" | "already-unsubscribed" | "invalid-token";
      email?: string | null;
    };

function getStatusContent(pageState: PageState) {
  switch (pageState.status) {
    case "success":
      return {
        title: "You are unsubscribed",
        description: `We paused weekly delivery${
          pageState.email ? ` for ${pageState.email}` : ""
        }. Your saved sources are still available if you ever opt back in.`,
      };
    case "already-unsubscribed":
      return {
        title: "You were already unsubscribed",
        description: `Weekly delivery was already paused${
          pageState.email ? ` for ${pageState.email}` : ""
        }. No further action is needed.`,
      };
    case "invalid-token":
      return {
        title: "This unsubscribe link is invalid",
        description:
          "The link may be incomplete or expired. Sign in to your account if you still need to change newsletter settings.",
      };
    default:
      return {
        title: "Updating your newsletter preferences…",
        description: "Please wait while we process your unsubscribe request.",
      };
  }
}

export default function NewsletterUnsubscribePage() {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>({ status: "loading" });

  useEffect(() => {
    let isActive = true;
    const token = searchParams.get("token");

    if (!token) {
      setPageState({ status: "invalid-token" });
      return () => {
        isActive = false;
      };
    }

    unsubscribeFromNewsletter({ token })
      .then((result: NewsletterUnsubscribeResult) => {
        if (!isActive) {
          return;
        }

        setPageState(result as NewsletterUnsubscribeResult);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setPageState({ status: "invalid-token" });
      });

    return () => {
      isActive = false;
    };
  }, [searchParams]);

  const content = getStatusContent(pageState);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center px-4 py-16">
      <div className="w-full rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-xs dark:border-neutral-800 dark:bg-boxdark">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          Optimistic News
        </p>
        <h1 className="mt-4 text-3xl font-bold text-neutral-900 dark:text-white">
          {content.title}
        </h1>
        <p className="mt-4 text-base text-neutral-600 dark:text-neutral-300">
          {content.description}
        </p>

        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <LinkButton to={routes.LandingPageRoute.to} fullWidth>
            Back to home
          </LinkButton>
          <LinkButton
            to={routes.NewsletterRoute.to}
            variant="secondary"
            fullWidth
          >
            Manage newsletter
          </LinkButton>
        </div>
      </div>
    </div>
  );
}
